import { Link } from 'gatsby';
import type { Ref } from 'react';
import React, { forwardRef } from 'react';

import AbbyChatLink, { isAbbyChatLink } from 'src/components/common/abby-chat/abby-chat-link';
import { useLink } from 'src/custom-hooks/use-link';
import { isExternalLink, isJumpLink } from 'src/helpers/link-helper';
import { updateUrlPreservingQueryParams } from 'src/helpers/query-string-helper';
import { matchReducedMotion } from 'src/styling/media-queries';

type WebsiteLinkProps = {
  children?: React.ReactNode;
  to: string;
  goToGlobal?: boolean;
  preserveQueryParams?: boolean;
  openInNewTab?: boolean;
  forceOpenInSameTab?: boolean;
  onFocus?: () => void;
  className?: string;
  style?: React.CSSProperties;
  isTabIndex?: boolean;
  handleClick?: (event: React.MouseEvent<HTMLElement>) => void;
};

export const WebsiteLink = forwardRef<HTMLElement, WebsiteLinkProps>((props, ref) => {
  if (isAbbyChatLink(props.to)) {
    return (
      <AbbyChatLink
        {...props}
        className={props.className}
        style={props.style}
        handleClick={props.handleClick}
      >
        {props.children}
      </AbbyChatLink>
    );
  }

  const isExternalOrJumpLink = props.to && (isExternalLink(props.to) || isJumpLink(props.to));

  return !isExternalOrJumpLink ? (
    <InternalLink
      {...props}
      ref={ref as React.ForwardedRef<Link<unknown>> & React.ForwardedRef<HTMLAnchorElement>}
    />
  ) : (
    <AnchorLink
      {...props}
      ref={ref as React.ForwardedRef<Link<unknown>> & React.ForwardedRef<HTMLAnchorElement>}
    />
  );
});

const InternalLink = forwardRef<HTMLAnchorElement, WebsiteLinkProps>((props, ref) => {
  const { translatedPath, useGatsbyPath } = useLink({
    toPath: props.to,
    goToGlobal: props.goToGlobal,
  });

  const useGatsbyLink = useGatsbyPath && !props.openInNewTab;

  const pathWithQueryParams = props.preserveQueryParams
    ? updateUrlPreservingQueryParams(translatedPath)
    : translatedPath;

  const tabIndexProp = props.isTabIndex ? { tabIndex: 0 } : {};

  return useGatsbyLink ? (
    <Link
      className={props.className}
      to={pathWithQueryParams}
      onFocus={props.onFocus}
      style={props.style}
      ref={ref as Ref<Link<unknown>> & Ref<HTMLAnchorElement>}
      onClick={(e) => props.handleClick?.(e)}
      {...tabIndexProp}
    >
      {props.children}
    </Link>
  ) : (
    <AnchorLink {...props} to={pathWithQueryParams} ref={ref} />
  );
});

export const ExternalLink = forwardRef<HTMLAnchorElement, WebsiteLinkProps>((props, ref) => (
  <AnchorLink {...props} ref={ref} />
));

const AnchorLink = forwardRef<HTMLAnchorElement, WebsiteLinkProps>((props, ref) => {
  const tabIndexProp = props.isTabIndex ? { tabIndex: 0 } : {};

  return (
    <a
      className={props.className}
      href={props.to}
      onFocus={props.onFocus}
      style={props.style}
      target={props.forceOpenInSameTab ? '_self' : props.openInNewTab ? '_blank' : undefined}
      rel={!props.forceOpenInSameTab && props.openInNewTab ? 'noopener noreferrer' : undefined}
      ref={ref}
      onClick={(event) => {
        props?.handleClick?.(event);

        if (event.defaultPrevented) {
          return;
        }

        if (isJumpLink(props.to)) {
          const id = props.to.split('#')[1];
          const prefersReducedMotion = matchReducedMotion().matches;
          document
            .getElementById(id)
            ?.scrollIntoView({ behavior: prefersReducedMotion ? 'instant' : 'smooth' });
          event.preventDefault();
        }
      }}
      {...tabIndexProp}
    >
      {props.children}
    </a>
  );
});

WebsiteLink.displayName = 'WebsiteLink';
InternalLink.displayName = 'InternalLink';
ExternalLink.displayName = 'ExternalLink';
AnchorLink.displayName = 'AnchorLink';
